import React, { useState } from 'react'
import { Dropdown, Switch, Typography } from 'antd'
import { TableEventListeners } from 'antd/lib/table'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Drawer,
  Menu,
  MenuItem,
  Text,
  ToolIcon
} from 'src/components/Ant'
import { ColumnProps } from 'src/components/Ant/Table'
import { Id, User } from 'src/types'
import { sendAgencyPartners } from 'src/core/store/ducks/partners/link-agency/actions'
import { sendPartner } from 'src/core/store/ducks/partners/actions'
import history from 'src/config/routes/history'

import * as CSS from './styles'

import { SideModal } from '../sideModal'

export interface DataSource {
  address_number: string
  address: string
  id: Id
  partner_name: string
  partner_type: string
  partner_vat_number: Id
  quarter: string
  status: Boolean
}

interface Props {
  currentPage: number
  dataSource: DataSource[]
  isNew: boolean
  loading: boolean
  onChange(page: number): void
  onRow?: (record: DataSource, index: number) => TableEventListeners
  openSideModal?: boolean
  responsePartner: any
  setOpenSideModal?: (openSideModal: boolean) => void
  totalPages: number
}

export default function Table({
  currentPage,
  dataSource,
  isNew,
  loading,
  onChange,
  openSideModal,
  responsePartner,
  setOpenSideModal,
  totalPages
}: Props) {
  const [partner] = useState<User>()

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const columns: ColumnProps<unknown>[] = [
    {
      title: 'web.table.head-partner.id',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'web.table.head-partner.partner_name.name',
      dataIndex: 'partner_name',
      key: 'partner_name'
    },
    {
      title: 'web.table.head-partner.partner_type',
      dataIndex: 'partner_type',
      key: 'partner_type'
    },
    {
      dataIndex: '',
      key: 'address',
      title: 'web.table.head-partner.address.name',
      render: (record: any) => (
        <Typography.Text>{`${record.address}, ${record.address_number}, ${record.quarter}. ${record.city_name} - ${record.state_name}`}</Typography.Text>
      )
    },
    {
      title: 'web.table.head-partner.status',
      dataIndex: 'status',
      key: 'status',
      render: (status: boolean, record: any) => (
        <Switch
          checked={status}
          checkedChildren={t('common.active')}
          data-test-id="antifraud-active"
          onChange={value => {
            dispatch(sendPartner({ ...record, active: value, status: value }))
          }}
          unCheckedChildren={t('common.inactive')}
        />
      )
    },
    {
      title: '',
      dataIndex: 'agency_partner',
      key: 'agency_partner',
      render: (agency_partner: any) =>
        agency_partner?.active && (
          <ToolIcon title="web.table.head-partner.partner-link" type="link" />
        )
    },
    {
      dataIndex: '',
      render: (record: any) => (
        <Dropdown
          overlay={
            <Menu
              onClick={async ({ key }: any) => {
                const { agency_partner_id } = record.agency_partner
                switch (key) {
                  case 'link-agency':
                    if (agency_partner_id) {
                      dispatch(
                        sendAgencyPartners({
                          agency_partner_id,
                          active: true
                        })
                      )
                    } else {
                      dispatch(
                        sendAgencyPartners({
                          partner_id: record.id,
                          active: true
                        })
                      )
                    }
                    break
                  case 'unlink-agency':
                    if (agency_partner_id) {
                      dispatch(
                        sendAgencyPartners({
                          agency_partner_id,
                          active: false
                        })
                      )
                    }
                    break
                  case 'view-room':
                    if (record.partner_hotels[0].partner_hotel_id) {
                      history.push(
                        `/agencies/partners/${record.id}/hotel/${
                          record.partner_hotels[0].partner_hotel_id
                        }/bedrooms/${agency_partner_id || 0}`
                      )
                    }
                    break
                }
              }}
            >
              <MenuItem
                key={
                  record.agency_partner?.active
                    ? 'unlink-agency'
                    : 'link-agency'
                }
                title={
                  record.agency_partner?.active
                    ? 'web.table.head-partner.unlink-agency'
                    : 'web.table.head-partner.link-agency'
                }
              />
              <MenuItem
                key="view-room"
                title="web.table.head-partner.view-room"
              />
            </Menu>
          }
          placement="bottomRight"
        >
          <Button type="link">
            <ToolIcon type="more" style={{ fontWeight: 'bold' }} />
          </Button>
        </Dropdown>
      ),
      width: 50
    }
  ]

  return (
    <>
      <CSS.Table
        className="table-striped"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        onChange={({ current }) => onChange(current)}
        pagination={{
          current: currentPage,
          pageSize: 10,
          total: totalPages
        }}
      />
      <Drawer
        onClose={() => {
          setOpenSideModal(false)
        }}
        title={
          <Text
            title={isNew ? 'common.actions.add' : 'common.actions.change'}
          />
        }
        visible={openSideModal}
        width="40%"
      >
        <SideModal
          isNew={isNew}
          partner={partner}
          responsePartner={responsePartner}
          setOpenSideModal={setOpenSideModal}
        />
      </Drawer>
    </>
  )
}
